<script setup lang="ts">
import { $getChangeRequestOptions } from '@/api/changeRequests'

interface IProps {
  modelValue?: any,
  label?: string,
  selectOptions?: any[] | null,
  isSelectAllVisible?: boolean,
  /**
   * Optional array of priority IDs that should be disabled for selection.
   * If null or undefined, no priorities will be disabled.
   */
  disabledPriorities?: number[] | null,
}

const props = withDefaults(defineProps<IProps>(), {
  modelValue: null,
  label: 'Priority',
  selectOptions: null,
  isSelectAllVisible: false,
  disabledPriorities: null,
})

interface CustomEvents {
  (e: 'update:modelValue', newValue: any): void
}

const emit = defineEmits<CustomEvents>()

interface Priority {
  value: number
  display_name: string
}

const priorities = ref<Priority[]>([])
const prioritiesLoading = ref<boolean>(false)

const priorityOptions = computed(() => {
  if (props.selectOptions) {
    return props.selectOptions
  } else {
    return priorities.value
  }
})

const localSearchStr = ref<string>('')

const filteredPriorities = computed(() => {
  if (!localSearchStr.value) return priorityOptions.value
  const searchLower = localSearchStr.value.toLowerCase()
  return priorityOptions.value.filter((option: Priority) =>
    option.display_name?.toLowerCase().includes(searchLower)
  )
})

const selectedValues = computed(() => Array.isArray(props.modelValue) ? props.modelValue : [])

const allFilteredSelected = computed(() => {
  if (!filteredPriorities.value.length) return false
  return filteredPriorities.value.every((option: Priority) =>
    selectedValues.value.some((selected: Priority) => selected.value === option.value)
  )
})

const someFilteredSelected = computed(() => {
  if (!filteredPriorities.value.length) return false
  const selectedCount = filteredPriorities.value.filter((option: Priority) =>
    selectedValues.value.some((selected: Priority) => selected.value === option.value)
  ).length
  return selectedCount > 0 && selectedCount < filteredPriorities.value.length
})

const toggleSelectAll = () => {
  if (allFilteredSelected.value || someFilteredSelected.value) {
    const newValue = selectedValues.value.filter((selected: Priority) =>
      !filteredPriorities.value.some((option: Priority) => option.value === selected.value)
    )
    emit('update:modelValue', newValue)
  } else {
    const newValue = [...selectedValues.value]
    filteredPriorities.value.forEach((option: Priority) => {
      if (!newValue.some((selected: Priority) => selected.value === option.value)) {
        newValue.push(option)
      }
    })
    emit('update:modelValue', newValue)
  }
}

const getPriorities = async () => {
  prioritiesLoading.value = true
  try {
    const res = await $getChangeRequestOptions()
    priorities.value = res.data.actions.POST.priority.choices
  } catch (e) {
    console.log(e)
  } finally {
    prioritiesLoading.value = false
  }
}

const onFocusPrioritySelect = async () => {
  if (!priorities.value.length && !props.selectOptions) {
    await getPriorities()
  }
}
</script>

<template>
  <VAutocomplete
    :model-value="modelValue"
    :items="[...filteredPriorities]"
    return-object
    item-title="display_name"
    item-value="value"
    :label="label"
    density="compact"
    multiple
    clearable
    @update:modelValue="newValue => $emit('update:modelValue', newValue)"
    @update:focused="onFocusPrioritySelect"
    @update:search="val => localSearchStr = val"
  >
    <template #prepend-item>
      <VListItem
        v-if="isSelectAllVisible && filteredPriorities?.length > 1"
        @click="toggleSelectAll"
        class="v-list-item--select-all"
      >
        <template #prepend>
          <VCheckboxBtn
            :model-value="allFilteredSelected"
            :indeterminate="someFilteredSelected"
          />
        </template>
        <VListItemTitle>
          {{ allFilteredSelected || someFilteredSelected ? 'Deselect' : 'Select' }} All
          ({{ filteredPriorities.length }} found)
        </VListItemTitle>
      </VListItem>
      <VDivider v-if="isSelectAllVisible && filteredPriorities?.length > 1" />
    </template>
    <template
      v-if="disabledPriorities"
      #item="{ item, index, props }"
    >
      <VListItem
        v-bind="props"
        :disabled="!!disabledPriorities ? disabledPriorities.includes(item?.value) : false"
      />
    </template>
    <template #selection="{ item, index }">
      <div class="d-flex align-center">
        <v-chip v-if="index < 1">
          <span>{{ item.title }}</span>
        </v-chip>
        <span
          v-if="index === 1"
          class="text-grey text-caption align-self-center"
        >
          (+{{ modelValue?.length - 1 }} others)
        </span>
      </div>
    </template>
    <template #no-data>
      <div
        v-if="!!prioritiesLoading"
        class="d-flex justify-center align-center"
      >
        Loading...
        <VProgressCircular
          class="margin-left-5"
          size="22"
          indeterminate
          color="white"
        />
      </div>
      <div
        v-else
        class="d-flex justify-center align-center"
      >
        Priorities not found
      </div>
    </template>
  </VAutocomplete>
</template>

<style lang="scss">

</style>
